import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Badge } from '@atoms/Badge';
import { Button } from '@atoms/Button';
import { Card } from '@atoms/Card';
import { Input } from '@atoms/Input';
import { Modal } from '@atoms/Modal';
import { Spinner } from '@atoms/Spinner';
import { adminBillingService } from '@services/adminBillingService';
import { getErrorMessage } from '@services/api';
import type { AdminRestaurantSubscription, AdminSubscriptionPlan } from '@/types';

interface PlanFormState {
  id?: string;
  name: string;
  description: string;
  price: string;
  isActive: boolean;
}

const EMPTY_PLAN_FORM: PlanFormState = { name: '', description: '', price: '', isActive: true };

function formatDate(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function accessBadge(row: AdminRestaurantSubscription) {
  switch (row.access.status) {
    case 'trial':
      return <Badge variant="info">Deneme ({row.access.trialDaysLeft} gün)</Badge>;
    case 'active':
      return row.access.cancelsAtPeriodEnd ? (
        <Badge variant="warning">İptal — dönem sonu</Badge>
      ) : (
        <Badge variant="success">Abone</Badge>
      );
    case 'expired':
      return <Badge variant="danger">Süresi doldu</Badge>;
    default:
      return <Badge>—</Badge>;
  }
}

export function BillingSection() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planForm, setPlanForm] = useState<PlanFormState>(EMPTY_PLAN_FORM);
  const [trialDaysInput, setTrialDaysInput] = useState('');
  const [trialModal, setTrialModal] = useState<{ restaurantId: string; name: string } | null>(null);
  const [trialEndInput, setTrialEndInput] = useState('');

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'billing'] });
  };

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['admin', 'billing', 'settings'],
    queryFn: () => adminBillingService.getSettings(),
  });

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['admin', 'billing', 'plans'],
    queryFn: () => adminBillingService.getPlans(),
  });

  const { data: subscriptions, isLoading: subsLoading } = useQuery({
    queryKey: ['admin', 'billing', 'subscriptions'],
    queryFn: () => adminBillingService.getSubscriptions(),
  });

  const savePlanMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: planForm.name.trim(),
        description: planForm.description.trim() || undefined,
        price: Number(planForm.price),
        isActive: planForm.isActive,
      };
      return planForm.id
        ? adminBillingService.updatePlan(planForm.id, payload)
        : adminBillingService.createPlan(payload);
    },
    onSuccess: () => {
      setPlanModalOpen(false);
      setPlanForm(EMPTY_PLAN_FORM);
      setError(null);
      invalidate();
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const deletePlanMutation = useMutation({
    mutationFn: (id: string) => adminBillingService.deletePlan(id),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const trialDaysMutation = useMutation({
    mutationFn: (days: number) => adminBillingService.setTrialDays(days),
    onSuccess: () => {
      setTrialDaysInput('');
      setError(null);
      invalidate();
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const enforcementMutation = useMutation({
    mutationFn: (enforced: boolean) => adminBillingService.setEnforcement(enforced),
    onSuccess: invalidate,
    onError: (err) => setError(getErrorMessage(err)),
  });

  const extendTrialMutation = useMutation({
    mutationFn: ({ restaurantId, trialEndsAt }: { restaurantId: string; trialEndsAt: string }) =>
      adminBillingService.extendTrial(restaurantId, trialEndsAt),
    onSuccess: () => {
      setTrialModal(null);
      setTrialEndInput('');
      setError(null);
      invalidate();
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const syncMutation = useMutation({
    mutationFn: () => adminBillingService.syncNow(),
    onSuccess: invalidate,
    onError: (err) => setError(getErrorMessage(err)),
  });

  if (settingsLoading || plansLoading || subsLoading) return <Spinner />;

  const openCreatePlan = () => {
    setPlanForm(EMPTY_PLAN_FORM);
    setPlanModalOpen(true);
  };

  const openEditPlan = (plan: AdminSubscriptionPlan) => {
    setPlanForm({
      id: plan.id,
      name: plan.name,
      description: plan.description ?? '',
      price: String(plan.price),
      isActive: plan.isActive,
    });
    setPlanModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Abonelik Yönetimi</h1>
          <p className="mt-1 text-sm text-slate-500">
            Restoran üyelik planları, deneme süresi ve aboneler
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          loading={syncMutation.isPending}
          onClick={() => syncMutation.mutate()}
        >
          <RefreshCw className="h-4 w-4" /> Iyzico ile senkronla
        </Button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Settings */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="card-elevated space-y-3 p-5">
          <h2 className="font-semibold text-slate-900">Deneme Süresi</h2>
          <p className="text-sm text-slate-600">
            Yeni restoran hesapları platformu <strong>{settings?.trialDays} gün</strong> ücretsiz
            kullanabilir.
          </p>
          <div className="flex items-end gap-2">
            <Input
              label="Gün"
              type="number"
              min={0}
              max={365}
              value={trialDaysInput || String(settings?.trialDays ?? '')}
              onChange={(e) => setTrialDaysInput(e.target.value)}
              className="w-32"
            />
            <Button
              loading={trialDaysMutation.isPending}
              onClick={() => trialDaysMutation.mutate(Number(trialDaysInput || settings?.trialDays))}
            >
              Kaydet
            </Button>
          </div>
        </Card>

        <Card className="card-elevated space-y-3 p-5">
          <h2 className="font-semibold text-slate-900">Üyelik Zorunluluğu</h2>
          <p className="text-sm text-slate-600">
            Açıkken, deneme süresi dolan ve aboneliği olmayan restoranlar sipariş veremez.
          </p>
          <div className="flex items-center gap-3">
            {settings?.enforced ? (
              <Badge variant="success">Aktif</Badge>
            ) : (
              <Badge variant="warning">Devre dışı</Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              loading={enforcementMutation.isPending}
              onClick={() => enforcementMutation.mutate(!settings?.enforced)}
            >
              {settings?.enforced ? 'Devre dışı bırak' : 'Aktifleştir'}
            </Button>
          </div>
        </Card>
      </div>

      {/* Plans */}
      <Card className="card-elevated overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h2 className="font-semibold text-slate-900">Üyelik Planları</h2>
          <Button size="sm" onClick={openCreatePlan}>
            <Plus className="h-4 w-4" /> Yeni Plan
          </Button>
        </div>
        {!plans?.length ? (
          <p className="p-4 text-sm text-slate-500">
            Henüz plan yok. Restoranların abone olabilmesi için en az bir plan oluşturun.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900">{plan.name}</p>
                    {plan.isActive ? (
                      <Badge variant="success">Satışta</Badge>
                    ) : (
                      <Badge>Pasif</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">
                    {new Intl.NumberFormat('tr-TR', {
                      style: 'currency',
                      currency: plan.currency,
                    }).format(plan.price)}
                    /ay · {plan.activeSubscriberCount} aktif abone
                    {plan.iyzicoPricingPlanReferenceCode ? ' · Iyzico senkron ✓' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEditPlan(plan)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600"
                    loading={
                      deletePlanMutation.isPending && deletePlanMutation.variables === plan.id
                    }
                    onClick={() => deletePlanMutation.mutate(plan.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Subscribers */}
      <Card className="card-elevated overflow-hidden">
        <div className="border-b border-slate-100 px-4 py-3">
          <h2 className="font-semibold text-slate-900">Restoranlar</h2>
        </div>
        {!subscriptions?.length ? (
          <p className="p-4 text-sm text-slate-500">Restoran hesabı bulunamadı.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs uppercase text-slate-500">
                  <th className="px-4 py-2.5">Restoran</th>
                  <th className="px-4 py-2.5">Durum</th>
                  <th className="px-4 py-2.5">Plan</th>
                  <th className="px-4 py-2.5">Deneme Bitişi</th>
                  <th className="px-4 py-2.5">Dönem Sonu</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {subscriptions.map((row) => (
                  <tr key={row.restaurantId}>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-slate-900">{row.name}</p>
                      <p className="text-xs text-slate-500">@{row.username}</p>
                    </td>
                    <td className="px-4 py-2.5">{accessBadge(row)}</td>
                    <td className="px-4 py-2.5 text-slate-700">
                      {row.subscription?.planName ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">
                      {formatDate(row.trialEndsAt ?? row.access.trialEndsAt)}
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">
                      {formatDate(row.subscription?.currentPeriodEnd)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setTrialModal({ restaurantId: row.restaurantId, name: row.name });
                          setTrialEndInput('');
                        }}
                      >
                        Deneme uzat
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Plan modal */}
      <Modal
        open={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        title={planForm.id ? 'Planı Düzenle' : 'Yeni Plan'}
      >
        <div className="space-y-3">
          <Input
            label="Plan Adı"
            value={planForm.name}
            onChange={(e) => setPlanForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Örn. Standart Üyelik"
          />
          <Input
            label="Açıklama"
            value={planForm.description}
            onChange={(e) => setPlanForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Plana dahil özellikler"
          />
          <Input
            label="Aylık Ücret (TL)"
            type="number"
            min={1}
            step="0.01"
            value={planForm.price}
            onChange={(e) => setPlanForm((f) => ({ ...f, price: e.target.value }))}
          />
          {planForm.id && (
            <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
              Fiyat değişikliği mevcut aboneleri etkilemez; yeni fiyat yalnızca yeni aboneliklerde
              geçerli olur.
            </p>
          )}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={planForm.isActive}
              onChange={(e) => setPlanForm((f) => ({ ...f, isActive: e.target.checked }))}
            />
            Satışta (restoranlar görebilir)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setPlanModalOpen(false)}>
              Vazgeç
            </Button>
            <Button
              loading={savePlanMutation.isPending}
              disabled={!planForm.name.trim() || !planForm.price || Number(planForm.price) <= 0}
              onClick={() => savePlanMutation.mutate()}
            >
              Kaydet
            </Button>
          </div>
        </div>
      </Modal>

      {/* Extend trial modal */}
      <Modal
        open={Boolean(trialModal)}
        onClose={() => setTrialModal(null)}
        title={`Deneme süresini uzat — ${trialModal?.name ?? ''}`}
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Bu restoran için yeni bir deneme bitiş tarihi belirleyin. Restoran bu tarihe kadar
            abonelik olmadan platformu kullanabilir.
          </p>
          <Input
            label="Deneme Bitiş Tarihi"
            type="date"
            value={trialEndInput}
            onChange={(e) => setTrialEndInput(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setTrialModal(null)}>
              Vazgeç
            </Button>
            <Button
              loading={extendTrialMutation.isPending}
              disabled={!trialEndInput}
              onClick={() => {
                if (!trialModal) return;
                const endOfDay = new Date(`${trialEndInput}T23:59:59`);
                extendTrialMutation.mutate({
                  restaurantId: trialModal.restaurantId,
                  trialEndsAt: endOfDay.toISOString(),
                });
              }}
            >
              Kaydet
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
